import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const headerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] } },
};

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 32, scale: 0.97 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

export default function ServicesSection() {
  const { t } = useTranslation();

  const services = [
    { icon: 'ri-code-s-slash-line', title: t('services.it.title'), desc: t('services.it.desc'), isGlass: true, href: '/services/it' },
    {
      icon: null,
      customIcon: (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-7 h-7">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" fill="currentColor" opacity="0.3"/>
          <path d="M8 12l2.5 2.5L16 9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 6v2M12 16v2M6 12h2M16 12h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
          <circle cx="12" cy="12" r="2.5" fill="currentColor" opacity="0.4"/>
          <path d="M7.05 7.05l1.41 1.41M15.54 15.54l1.41 1.41M7.05 16.95l1.41-1.41M15.54 8.46l1.41-1.41" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" opacity="0.5"/>
        </svg>
      ),
      title: t('services.mediation.title'), desc: t('services.mediation.desc'), isGlass: false, href: '/services/mediation'
    },
    { icon: 'ri-lightbulb-line', title: t('services.consulting.title'), desc: t('services.consulting.desc'), isGlass: true, href: '/services/consulting' },
    { icon: 'ri-pen-nib-line', title: t('services.technical.title'), desc: t('services.technical.desc'), isGlass: false, href: '/services/technical' },
    { icon: 'ri-megaphone-line', title: t('services.marketing.title'), desc: t('services.marketing.desc'), isGlass: true, href: '/services/marketing' },
    { icon: 'ri-robot-line', title: t('services.automation.title'), desc: t('services.automation.desc'), isGlass: false, href: '/services/automation' },
  ];

  return (
    <section id="services" className="py-28 lg:py-36 bg-[#2d3139]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          variants={headerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="text-center mb-16"
        >
          <h2 className="text-white font-bold text-4xl md:text-5xl mb-6">
            {t('services.title')}
          </h2>
          <p className="text-[rgba(255,255,255,0.5)] text-lg max-w-[600px] mx-auto">
            {t('services.subtitle')}
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 auto-rows-fr"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.05 }}
        >
          {services.map((service, index) => {
            const cardContent = (
              <motion.div
                key={index}
                variants={cardVariants}
                whileHover={{ y: -5, transition: { duration: 0.15, ease: 'easeOut' } }}
                className={`group relative p-12 rounded-[20px] flex flex-col h-full ${
                  service.isGlass
                    ? 'bg-[rgba(255,255,255,0.1)] backdrop-blur-md border border-[rgba(255,255,255,0.15)]'
                    : 'bg-[#0f1419]'
                } hover:shadow-[0_8px_30px_rgba(0,212,255,0.15)] transition-shadow duration-200 cursor-pointer overflow-hidden`}
              >
                {/* Hover Accent Line */}
                <div className="absolute top-0 left-0 right-0 h-[3px] bg-[#00d4ff] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>

                {/* Icon */}
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[rgba(0,212,255,0.2)] mb-7 text-white shrink-0">
                  {service.customIcon ? (
                    service.customIcon
                  ) : (
                    <i className={`${service.icon} text-white text-3xl`}></i>
                  )}
                </div>

                {/* Content */}
                <h3 className="text-white text-2xl font-bold mb-4">{service.title}</h3>
                <p className="text-[rgba(255,255,255,0.65)] text-base leading-relaxed mb-6 flex-grow">
                  {service.desc}
                </p>

                {/* Learn More Link */}
                <div className="flex items-center gap-2 text-[#00d4ff] text-sm font-medium group-hover:gap-3 transition-all duration-300 whitespace-nowrap mt-auto">
                  {t('services.learnMore')}
                  <i className="ri-arrow-right-line text-lg"></i>
                </div>
              </motion.div>
            );

            return service.href ? (
              <Link key={index} to={service.href} className="flex h-full no-underline">
                {cardContent}
              </Link>
            ) : (
              <div key={index} className="flex h-full">
                {cardContent}
              </div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
